import {
  ListBoxItem as AriaListBoxItem,
  composeRenderProps,
  type ListBoxItemProps,
} from "react-aria-components";
import {
  Select,
  SelectListBox,
  SelectPopover,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { NewsFeedItem } from "@/data-access/news-feeds/queries/news-feeds-queries";
import { cn } from "@/lib/utils";

export interface NewsFeedSwitcherProps {
  /** List of feeds. */
  feeds: NewsFeedItem[];
  /** Currently selected feed ID. */
  selectedFeedId?: string | null;
  /** Called when the user selects a different feed. */
  onFeedChange?: (feedId: string) => void;
  /** When true, show feed image or first-letter-in-square fallback in trigger and list items. Default false. */
  displayImages?: boolean;
  /** Applied to the trigger wrapper. */
  className?: string;
}

const FEED_IMAGE_SIZE = 24;

/** Hashes name to a number. */
const hashName = (name: string) => {
  let n = 0;
  for (let i = 0; i < name.length; i += 1)
    n = (n * 31 + name.charCodeAt(i)) >>> 0;
  return n;
};

/** Stable random hex from name hash; range kept dark so white text is readable. */
const nameToHex = (name: string) => {
  const h = hashName(name);
  const r = 48 + ((h >>> 0) % 120);
  const g = 48 + ((h >>> 8) % 120);
  const b = 48 + ((h >>> 16) % 120);
  return [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
};

const feedInitial = (name: string) =>
  name.trim().slice(0, 1).toUpperCase() || "?";

/**
 * Renders feed image when present; fallback is a coloured square with the first letter.
 */
const FeedImage = ({
  image,
  name,
}: {
  image?: string | null;
  name: string;
}) => {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="size-6 shrink-0 rounded-sm object-contain"
        width={FEED_IMAGE_SIZE}
        height={FEED_IMAGE_SIZE}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={name}
      className="flex size-6 shrink-0 items-center justify-center rounded-sm text-[10px] font-medium leading-none text-white"
      style={{ backgroundColor: `#${nameToHex(name)}` }}
    >
      {feedInitial(name)}
    </span>
  );
};

/**
 * Select trigger and list use internal items with id + name + image.
 */
type InternalItem = NewsFeedItem;

/**
 * Nav dropdown to switch between news feeds. Uses React Aria Select.
 * Trigger shows selected feed image (or fallback) + name; dropdown lists all feeds with optional images.
 */
export const NewsFeedSwitcher = ({
  feeds,
  selectedFeedId = null,
  onFeedChange,
  displayImages = false,
  className,
}: NewsFeedSwitcherProps) => {
  const items: InternalItem[] = feeds;
  const selectedKey =
    selectedFeedId && feeds.some((f) => f.id === selectedFeedId)
      ? selectedFeedId
      : (feeds[0]?.id ?? null);

  const onSelectionChange = (key: React.Key | null) => {
    if (key !== null) {
      onFeedChange?.(String(key));
    }
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <Select<InternalItem>
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChange}
      aria-label="Switch news feed"
      className={cn("w-fit", className)}
    >
      <SelectTrigger
        size="sm"
        aria-label="Switch news feed"
        className={cn(
          "w-fit min-w-0 cursor-pointer border border-transparent bg-transparent shadow-none rounded-[8px]! gap-2",
          "hover:bg-gray-100",
          "data-[pressed=true]:border-violet-700",
        )}
      >
        <SelectValue<InternalItem>>
          {({ selectedItems }) => {
            const item = selectedItems[0] ?? null;
            return item ? (
              <span className="flex min-w-0 items-center gap-2">
                {displayImages && (
                  <FeedImage image={item.image} name={item.name} />
                )}
                <span className="whitespace-nowrap text-xs text-slate-900">
                  {item.name}
                </span>
              </span>
            ) : (
              <span className="text-xs text-slate-500">Select feed</span>
            );
          }}
        </SelectValue>
      </SelectTrigger>
      {/* Width: content-sized on small screens, min 315px from sm and up. Overrides base w-[--trigger-width]. */}
      <SelectPopover
        className={cn(
          "mt-1 w-max min-w-0 rounded-[12px] border border-gray-100 bg-white px-0 py-0 pb-4 shadow-xs sm:min-w-[315px]",
        )}
        placement="bottom start"
      >
        <div className="px-4 pt-4 pb-2 text-xxs font-medium uppercase tracking-wide text-slate-500">
          Newsfeeds
        </div>
        <SelectListBox<InternalItem>
          items={items}
          className="max-h-[min(60vh,400px)] overflow-x-visible overflow-y-auto px-1 py-1.5"
        >
          {(item) => (
            <NewsFeedSwitcherItem
              key={item.id}
              item={item}
              displayImages={displayImages}
            />
          )}
        </SelectListBox>
      </SelectPopover>
    </Select>
  );
};

interface NewsFeedSwitcherItemProps extends ListBoxItemProps<InternalItem> {
  item: InternalItem;
  displayImages: boolean;
}

/**
 * Single feed option in the dropdown: image + name, with left purple bar and "Viewing" when selected.
 */
const NewsFeedSwitcherItem = ({
  item,
  displayImages,
  className,
  ...props
}: NewsFeedSwitcherItemProps) => (
  <AriaListBoxItem
    id={item.id}
    textValue={item.name}
    className={composeRenderProps(className, (c) =>
      cn(
        "relative flex w-full cursor-pointer select-none items-center gap-2 overflow-visible! rounded-xs py-2.5 text-gray-800 outline-hidden",
        "hover:bg-gray-100 hover:rounded-md",
        c,
      ),
    )}
    {...props}
  >
    {(renderProps) => (
      <span className="relative flex min-h-full w-full items-center gap-2">
        {renderProps.isSelected && (
          <span
            aria-hidden
            className="absolute top-0 z-10 h-full"
            style={{
              left: "-4px",
              width: 2,
              backgroundColor: "#6d28d9",
            }}
          />
        )}
        <span
          className={cn(
            "flex min-h-full w-full items-center gap-2 px-3",
            renderProps.isSelected && "pl-[14px]",
          )}
        >
          {displayImages && <FeedImage image={item.image} name={item.name} />}
          <span className="min-w-0 flex-1 whitespace-nowrap text-sm text-gray-900">
            {item.name}
          </span>
          {renderProps.isSelected && (
            <span className="shrink-0 text-xs text-blue-600">Viewing</span>
          )}
        </span>
      </span>
    )}
  </AriaListBoxItem>
);
